import { useIntl } from "react-intl";
import { SetStateAction, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Loader } from "@/components/common/loader/Loader";
import { Invoice } from "@/interfaces/invoice";
import { StrapiImage } from "@/utils/strapi";
import { BillingData, UnpaidInvoice } from "../AccountB2B";
import { Contract } from "./contract/Contract";
import { CalendarNav } from "./calendarNav/CalendarNav";
import { QuickActions } from "./quickActions/QuickActions";
import styles from "./Billing.module.scss";
import { NoContract } from "./contract/NoContract";
import { defaultCategoryImage } from "../../reservation/categories/agencyOption/defaultImages";

export type BillingProps = {
  data: BillingData | undefined;
  unpaidData: UnpaidInvoice[] | undefined;
  month: number;
  setMonth: React.Dispatch<SetStateAction<number>>;
  year: number;
  setYear: React.Dispatch<SetStateAction<number>>;
  isLoading: boolean;
  categoriesImages: {
    [name: string]: StrapiImage[] | undefined;
  };
};

export const Billing = ({
  data,
  unpaidData,
  year,
  month,
  setYear,
  setMonth,
  isLoading,
  categoriesImages,
}: BillingProps) => {
  const intl = useIntl();

  const [startDate, setStartDate] = useState<Dayjs>(
    dayjs()
      .set("year", year)
      .set("month", month)
      .set("date", 1)
      .set("hour", 0)
      .set("minute", 0)
      .set("second", 0)
  );
  const [endDate, setEndDate] = useState<Dayjs>(
    dayjs()
      .set("year", year)
      .set("month", month + 1)
      .set("date", 1)
      .set("hour", 0)
      .set("minute", 0)
      .set("second", 0)
  );
  const [count, setCount] = useState<number>(0);
  const [unpaidCount, setUnpaidCount] = useState<number>(0);
  const [unpaidTotal, setUnpaidTotal] = useState<number>(0);
  const [itemsChecked, setItemsChecked] = useState<string[]>([]);

  const setInfosFromDates = () => {
    let unpaidCountTmp = 0;
    let unpaidTotalTmp = 0;

    const countInvoices = (invoice: Invoice) => {
      if (itemsChecked.includes(invoice.id)) {
        if (!invoice.isBalanced && invoice.balance > 0) {
          unpaidCountTmp++;
          if (invoice.balance > 0) {
            unpaidTotalTmp += invoice.balance;
          }
        }
      }
    };

    data?.invoicesWithContract.map((d) => {
      d.invoices.map(countInvoices);
    });

    data?.invoicesWithoutContract.map(countInvoices);

    setUnpaidCount(unpaidCountTmp);
    setUnpaidTotal(unpaidTotalTmp);
  };

  const getInvoicesCount = () => {
    let countWCTmp = 0;

    const filterInvoices = (invoice: Invoice) =>
      dayjs(invoice.invoiceDate).isAfter(startDate) &&
      dayjs(invoice.invoiceDate).isBefore(endDate);

    data?.invoicesWithContract.map((contract) => {
      countWCTmp += contract.invoices.filter(filterInvoices).length;
    });

    const countWOCTmp =
      data?.invoicesWithoutContract.filter(filterInvoices).length ?? 0;

    setCount(countWCTmp + countWOCTmp);
  };

  const checkAtStart = () => {
    const ids: string[] = [];

    const checkInvoices = (invoice: Invoice) => {
      if (
        dayjs(invoice.invoiceDate).isAfter(startDate) &&
        dayjs(invoice.invoiceDate).isBefore(endDate) &&
        !invoice.isBalanced &&
        invoice.balance > 0
      ) {
        ids.push(invoice.id);
      }
    };

    data?.invoicesWithContract.map((contract) => {
      contract.invoices.map(checkInvoices);
    });

    data?.invoicesWithoutContract.map(checkInvoices);

    setItemsChecked(ids);
  };

  useEffect(() => {
    setStartDate(
      dayjs()
        .set("year", year)
        .set("month", month)
        .set("date", 1)
        .set("hour", 0)
        .set("minute", 0)
        .set("second", 0)
    );
    setEndDate(
      dayjs()
        .set("year", year)
        .set("month", month + 1)
        .set("date", 1)
        .set("hour", 0)
        .set("minute", 0)
        .set("second", 0)
    );
  }, [year, month]);

  useEffect(() => {
    setInfosFromDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsChecked, startDate, endDate]);

  useEffect(() => {
    getInvoicesCount();
    checkAtStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, startDate, endDate]);

  return (
    <div className={styles.billing}>
      <div className={styles.label}>
        {intl.formatMessage({ id: "myBillings" })}
      </div>
      <CalendarNav
        unpaidData={unpaidData}
        month={month}
        setMonth={setMonth}
        year={year}
        setYear={setYear}
      />
      <QuickActions
        month={month}
        year={year}
        count={count}
        unpaidCount={unpaidCount}
        unpaidTotal={unpaidTotal}
        itemsChecked={itemsChecked}
      />
      {!isLoading &&
        data?.invoicesWithContract.map((d, index) => {
          const formattedCode = `${d.contract?.categoryId ?? ""}-${
            d.contract?.franchiseId ?? ""
          }`;

          const categoryImages = categoriesImages[formattedCode] ??
            categoriesImages[`${d.contract?.categoryId ?? ""}-DEFAULT`] ?? [
              defaultCategoryImage,
            ];

          return (
            <Contract
              invoiceData={d}
              key={`contract-${index}`}
              startDate={startDate}
              endDate={endDate}
              images={categoryImages}
              itemsChecked={itemsChecked}
              setItemsChecked={setItemsChecked}
            />
          );
        })}
      {!isLoading && data?.invoicesWithoutContract && (
        <NoContract
          invoices={data.invoicesWithoutContract}
          startDate={startDate}
          endDate={endDate}
          itemsChecked={itemsChecked}
          setItemsChecked={setItemsChecked}
        />
      )}
      {isLoading && (
        <div className={styles.loading}>
          <Loader />
        </div>
      )}
    </div>
  );
};
